import { itemcollection, itemmap } from 'data/items'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import * as d3 from 'd3'
import { useElementSize, useWindowSize } from 'usehooks-ts'
import { formatNumber } from 'components/Utils/formatNumber'
import { ChartFrame } from 'components/ChartFrame'
import { TextWidth, TextWidthExtended } from '@ta-interaktiv/react-disco-grid'
import { collectionsmap } from 'data/collections'
import { Stink } from 'components/Stink'

var ttheight = 180
const Kehricht = ({ products }: { products: DBItems }) => {
  const [treedata, setTreedata] = useState<any>(undefined)
  const [ref, elementSize] = useElementSize()
  const windowSize = useWindowSize()
  const isMobile = windowSize.width < 712
  const [tooltipdata, setTooltipdata] = useState<any>(undefined)
  const [itemcount, setItemcount] = useState<number>(0)
  const [itemsummary, setItemsummary] = useState<{
    correct: number
    ok: number
    wrong: number
  }>({ correct: 0, ok: 0, wrong: 0 })
  const svgRef = useRef<SVGSVGElement>(null)
  const width = elementSize.width
  const height = 600
  const ttwidth = isMobile ? elementSize.width - 20 : 300

  function closeTooltip() {
    setTooltipdata(undefined)
  }

  function prepareTreeData() {
    const products_correct = itemcollection.filter(
      (f) => f.correct.includes('rest') && f.correct.length === 1
    )
    const products_ok = itemcollection.filter(
      (f) => f.correct.includes('rest') && f.correct.length > 1
    )
    const products_wrong = itemcollection.filter(
      (f) => !f.correct.includes('rest')
    )

    let collected_tree = {
      name: 'Kehricht',
      children: [
        { name: 'correct', children: [] },
        { name: 'ok', children: [] },
        { name: 'wrong', children: [] },
      ],
    } as any

    products_correct.forEach((p) => {
      if (!products[p.id]) return
      var count = products[p.id].correct
      if (count > 0) {
        collected_tree.children[0].children.push({
          name: p.id,
          value: count,
          category: 'correct',
        })
      }
    })
    products_ok.forEach((p) => {
      if (!products[p.id]) return
      var count = products[p.id].correct
      if (count > 0) {
        collected_tree.children[1].children.push({
          name: p.id,
          value: count,
          category: 'ok',
        })
        // collected_tree.ok.push({ name: p.id, value: count, category: 'ok' })
      }
    })

    products_wrong.forEach((p) => {
      if (!products[p.id] || !products[p.id].wrongAnswers) return
      var count = products[p.id].wrongAnswers['rest']
      if (count > 0) {
        collected_tree.children[2].children.push({
          name: p.id,
          value: count,
          category: 'wrong',
        })
      }
    })

    collected_tree.children[0].children.sort(
      (a: any, b: any) => b.value - a.value
    )
    collected_tree.children[1].children.sort(
      (a: any, b: any) => b.value - a.value
    )
    collected_tree.children[2].children.sort(
      (a: any, b: any) => b.value - a.value
    )

    const correct_count = collected_tree.children[0].children
      .map((m: any) => m.value)
      .reduce((a: any, b: any) => a + b, 0)

    const ok_count = collected_tree.children[1].children
      .map((m: any) => m.value)
      .reduce((a: any, b: any) => a + b, 0)

    const wrong_count = +collected_tree.children[2].children
      .map((m: any) => m.value)
      .reduce((a: any, b: any) => a + b, 0)

    const count = correct_count + ok_count + wrong_count

    setItemcount(count)

    // add percent to all items
    collected_tree.children.forEach((c: any) => {
      c.children.forEach((cc: any) => {
        cc.percent = Math.round((cc.value * 100) / count)
      })
    })
    setTreedata(collected_tree)

    // summary

    const correct_percent = (correct_count * 100) / count
    const ok_percent = (ok_count * 100) / count
    const wrong_percent = (wrong_count * 100) / count

    setItemsummary({
      correct: +correct_percent.toFixed(0),
      ok: +ok_percent.toFixed(0),
      wrong: +wrong_percent.toFixed(0),
    })
  }

  useEffect(() => {
    prepareTreeData()
  }, [products])

  useEffect(() => {
    if (!treedata) return
    const svgroot = d3
      .select(svgRef.current)
      .on('pointerout', isMobile ? () => {} : () => setTooltipdata(undefined))

    // treemap group
    const svg = svgroot.select('.treemap')

    svg.html('')

    svg.attr('width', width).attr('height', height)

    const root = d3.hierarchy(treedata).sum((d) => d.value)
    // .sort((a: any, b: any) => b.value - a.value)

    const treemapRoot = d3
      .treemap()
      .size([width, height])
      .tile(d3.treemapSquarify.ratio(2))
      .padding(1)
      .paddingOuter(1)(root)

    const nodes = svg
      .selectAll('g')
      .data(treemapRoot.leaves())
      .join('g')
      .attr('transform', (d) => `translate(${d.x0},${d.y0})`)

    const colorScale = d3.scaleOrdinal([
      'var(--correct-color)',
      '#E8E4D4',
      'var(--wrong-color)',
    ])

    const setTooltip = (d: any, i: any) => {
      // if (isMobile && tooltipdata) return
      setTooltipdata({
        data: i.data,
        x: i.x0,
        y: i.y0,
        width: i.x1 - i.x0,
        height: i.y1 - i.y0,
      })
    }

    nodes
      .append('rect')
      .attr('data-data', (d: any) => d.data.name)
      .attr('width', (d) => d.x1 - d.x0)
      .attr('height', (d) => d.y1 - d.y0)
      .attr('fill', (d: any) => colorScale(d.data.category))
      .on('touchend', setTooltip)
      .on('pointermove', isMobile ? () => {} : setTooltip)

    nodes
      .append('image')
      // .attr("pointer-events", "none")
      .attr('href', (d: any) => {
        if (!itemmap[d.data.name]) return ''
        return itemmap[d.data.name].image
          ? require(`../../../images/Products/${
              itemmap[d.data.name].image
            }.png`)
          : ''
      })
      .attr('visibility', (d: any) => {
        if (!itemmap[d.data.name]) return 'hidden'
        return itemmap[d.data.name].image ? 'visible' : 'hidden'
      })
      .attr('width', (d: any) => (d.x1 - d.x0) * 0.9)
      .attr('height', (d: any) => (d.y1 - d.y0) * 0.9)
      .attr('x', (d: any) => (d.x1 - d.x0) * 0.05)
      .attr('y', (d: any) => (d.y1 - d.y0) * 0.05)
      .attr('pointer-events', 'none')
  }, [treedata, width])

  var ttpos = { x: 15, y: 15 }

  if (svgRef.current && tooltipdata) {
    var bb = svgRef.current?.getBoundingClientRect()

    if (tooltipdata.x + ttwidth > bb?.width) {
      ttpos = {
        x: isMobile ? 0 : -ttwidth + tooltipdata.width - 15,
        y: isMobile ? tooltipdata.height + 3 : 15,
      }
    } else {
      ttpos = { x: isMobile ? 0 : 15, y: 15 }
    }

    if (tooltipdata.y + ttheight + 250 > bb?.height) {
      ttpos = {
        x: ttpos.x,
        y: isMobile ? -ttheight - 1 : -ttheight + tooltipdata.height - 15,
      }
    }
  }
  return (
    <>
      <KehrichtContainer>
        <TextWidth>
          <ChartFrame
            title='So würde der Kehricht aller Teilnehmenden aussehen'
            lead={
              <>
                <b>{itemsummary.correct + itemsummary.ok} Prozent</b> der
                Produkte, welche die Leserinnen und Leser in den Haushaltsmüll
                geschmissen haben, dürfen tatsächlich so entsorgt werden. Bei{' '}
                <b>
                  {formatNumber(
                    (itemsummary.ok * 100) /
                      (itemsummary.correct + itemsummary.ok)
                  )}{' '}
                  Prozent
                </b>{' '}
                davon hätte es allerdings noch eine bessere, nachhaltigere
                Lösung gegeben.
                <br />
                <br /> Und <b>{formatNumber(itemsummary.wrong)} Prozent</b> der
                Produkte, die im Kehricht landeten, gehören definitiv nicht hier
                hin. Für sie gibt es separate Sammlungen, mit denen das Material
                recycelt werden kann.
                <ChartTitle>
                  Produkte, die im Kehricht entsorgt wurden
                </ChartTitle>
                <ChartSub>Anteil an den Zuordnungen, in Prozent</ChartSub>
              </>
            }
            lang='de'
            colorKeyEntries={[
              { color: 'var(--correct-color)', label: 'richtig zugeordnet' },
              {
                color: '#E8E4D4',
                label: 'es gäbe eine nachhaltigere   Alternative',
              },

              { color: 'var(--wrong-color)', label: 'falsch zugeordnet' },
            ]}
          ></ChartFrame>
        </TextWidth>
      </KehrichtContainer>

      <TextWidthExtended>
        <div style={{ width: '100%', marginBottom: '60px' }} ref={ref}>
          <TreemapSVG ref={svgRef}>
            <g className='treemap' />
            {tooltipdata && (
              <Tooltip
                isMobile={isMobile}
                position={ttpos}
                data={tooltipdata}
                total={itemcount}
                ttwidth={ttwidth}
                closeTT={closeTooltip}
              />
            )}
          </TreemapSVG>
        </div>
      </TextWidthExtended>
      <TextWidth>
        <Text>
          <p>
            Was sehr oft im Kehricht entsorgt wird, ist Plastik. Denn die
            separate Sammlung von Kunststoffprodukten und -verpackungen ist
            (abgesehen von der Plastikflaschenrückgabe) in der Schweiz noch
            nicht etabliert. Es passiert momentan aber viel in diesem Bereich.
            Immer mehr Gemeinden und Unternehmen lancieren eigene, regionale
            Projekte und verkaufen Gemischt-Kunststoffsammelsäcke.
          </p>
          <p>
            Das ist grundsätzlich positiv. Laut der Initiative{' '}
            <Stink href='https://www.sammelsack.ch/faq'>
              «Bring Plastic Back»
            </Stink>{' '}
            muss ein Teil des gesammelten Kunststoffes allerdings im Ausland
            weiter&shy;verarbeitet werden, weil es in der Schweiz noch keine
            Recycling&shy;unternehmen gibt, die auf Polystyrol (zum Beispiel in
            Joghurtbechern) spezialisiert sind. Zudem stört und verteuert
            Plastik, welcher nicht in der korrekten Wertstoffsammlung entsorgt
            wird, den Recyclingprozess.
          </p>
          <p>
            «Wenn Sie unsicher sind, in welche Sammlung der Kunststoff gehört,
            ist der Hauskehricht immer die beste Lösung», schreibt deshalb{' '}
            <Stink href='https://swissrecycle.ch/de/wertstoffe-wissen/wertstoffe/kunststoff'>
              Swiss Recycle
            </Stink>
            , der Dachverband der Schweizer Recyclingorganisationen.
          </p>
        </Text>
      </TextWidth>
    </>
  )
}
const ChartTitle = styled.div`
  font-family: var(--font-sans-serif);
  font-size: 20px;
  font-weight: 700;
  margin-top: 30px;
`
const ChartSub = styled.div`
  font-family: var(--font-sans-serif);
  font-size: 15px;
  margin-bottom: -30px;
  line-height: 2em;
`
const Text = styled.div`
  font-family: var(--font-serif);
  a:-webkit-any-link {
    text-decoration: none;
    letter-spacing: 0 !important;
  }

  a:-webkit-any-link:hover,
  a:-webkit-any-link:link,
  a:-webkit-any-link:active,
  a:-webkit-any-link:visited {
    background-image: linear-gradient(
      90deg,
      var(--link-background),
      var(--link-background)
    );
    background-repeat: repeat-x;
    background-position: bottom;
    background-size: var(--link-background-size);
    transition: all 0.1s ease-in-out;
    color: var(--link-color) !important;
    background-origin: content-box;
  }
  a:-webkit-any-link:hover {
    background-size: var(--link-background-size-hover);
  }
  a.dark:-webkit-any-link:hover,
  a.dark:-webkit-any-link:link,
  a.dark:-webkit-any-link:active,
  a.dark:-webkit-any-link:visited {
    background-image: linear-gradient(90deg, #9698a8, #9698a8);
    color: #e9e9e9;
  }
`
const Tooltip = ({
  position,
  data,
  total,
  ttwidth,
  isMobile,
  closeTT,
}: {
  data: any
  total: number
  ttwidth: number
  position: { x: number; y: number }
  isMobile: boolean
  closeTT?: () => void
}) => {
  if (!data) return null
  const { x, y, width, height } = data
  const content = data.data
  const { name, value, category } = content
  var h = ttheight

  var nachaltiger =
    category === 'ok'
      ? itemmap[name].correct.filter((f) => f !== 'rest')
      : itemmap[name].correct
  console.log(data)
  return (
    <g transform={`translate(${x},${y})`} pointerEvents={'none'}>
      <rect width={width} height={height} fill='#202346' opacity={0.2} />
      <g
        transform={`translate(${position.x - (isMobile ? x - 10 : 0)},${
          position.y
        })`}
      >
        <foreignObject
          x={0}
          y={0}
          width={ttwidth}
          height={h}
          style={{
            backgroundColor: 'var(--site-background)',
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)',
          }}
        >
          <TooltipContent>
            <TitleRow
              style={{
                borderLeft:
                  '15px solid ' +
                  (category === 'correct'
                    ? 'var(--correct-color)'
                    : category === 'ok'
                    ? '#E8E4D4'
                    : 'var(--wrong-color)'),
              }}
            >
              <Title>
                {formatNumber((value * 100) / total)}%<br />
                {itemmap[name].name_de}
              </Title>
              {isMobile && <CloseButton onClick={closeTT}>X</CloseButton>}
            </TitleRow>
            {nachaltiger.length > 0 && category!=="correct" && (
              <>
                <span>
                  {category === 'wrong' ? 'Richtig' : 'Nachhaltiger:'}
                </span>
                <br />
                {nachaltiger.map((c) => {
                  return (
                    <>
                      <div className='list'>{collectionsmap[c].name_de}</div>
                    </>
                  )
                })}
              </>
            )}
          </TooltipContent>
        </foreignObject>
      </g>
    </g>
  )
}

const TitleRow = styled.div`
  display: flex;
  padding-left: 8px;
`

const Title = styled.div`
  flex: 0 0 calc(100% - 50px);
  font-weight: 700;
`

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  font-size: 22px;
  color: var(--text-color);
  border: 0;
  pointer-events: all;
  text-align: center;
`
const TreemapSVG = styled.svg`
  width: 100%;
  height: 600px;
  overflow: visible;
  font-size: 15px;
  border-radius: 15px;
  overflow: hidden;
  .b {
    font-weight: 700;
  }
  -webkit-box-shadow: 0px 5px 22px 0px rgba(0, 0, 0, 0.15);
  box-shadow: 0px 5px 22px 0px rgba(0, 0, 0, 0.15);
`
const TooltipContent = styled.div`
  padding: 10px;
  line-height: 1.2em;
  background-color: var(--site-background);
  display: inline-block;
  width: calc(100% - 20px);
  .list {
    line-height: 1.4em;
    font-size: 13px;
  }
  span {
    display: inline-block;
    color: var(--gray-3);
    font-size: 14px;
    margin-bottom: 3%;
    margin-top: 15px;
  }
`

const KehrichtContainer = styled.div`
  /* display: flex;
  flex-wrap: wrap;
  width: 100%;
  font-size: 8px;
  line-height: 10px;
  margin-bottom: 200px; */
`

export default Kehricht
